import { useEffect, useState } from "react";
import { useSessionStorage } from "@mantine/hooks";

import { SESSION_STORAGE_KEYS } from "@constants/index";

import { useUpdateAppointment } from "./mutations/appointment/useUpdateAppointment";


const removeThreshold = () => {
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.VC_THRESHOLD);
};

interface UseVideoCallThresholdProps {
  openVideoCallFeedbackModal: () => void;
  hasCallEnded: boolean;
  appointmentId?: string;
  callDuration?: number;
}

export const useVideoCallThreshold = ({
  hasCallEnded,
  callDuration,
  appointmentId = "",
}: UseVideoCallThresholdProps) => {
  const [videoCallThreshold, setVideoCallThreshold] = useSessionStorage({
    key: SESSION_STORAGE_KEYS.VC_THRESHOLD,
    defaultValue: {
      [SESSION_STORAGE_KEYS.VC_DOCTOR_JOINED]: false,
      [SESSION_STORAGE_KEYS.VC_PATIENT_JOINED]: false,
      [SESSION_STORAGE_KEYS.VC_CALL_DURATION_WITH_PATIENT]: 0,
    },
  });
  const [canOpenFeedbackModal, setCanOpenFeedbackModal] = useState(false);
  const [canChangeApptCallStatus, setCanChangeApptCallStatus] = useState(false);

  const { mutate: updateAppointment } = useUpdateAppointment();

  useEffect(() => {
    // If doctor and patient joins and at least 5 seconds have elapsed, allow displaying feedback modal
    if (
      videoCallThreshold[SESSION_STORAGE_KEYS.VC_DOCTOR_JOINED] &&
      videoCallThreshold[SESSION_STORAGE_KEYS.VC_PATIENT_JOINED] &&
      videoCallThreshold[SESSION_STORAGE_KEYS.VC_CALL_DURATION_WITH_PATIENT] > 5
    ) {
      import.meta.env && console.log("Can open feedback modal");
      setCanOpenFeedbackModal(true);
    }

    // If doctor and patient joins and at least 1 minute has elapsed, allow changing appointment call status
    if (
      videoCallThreshold[SESSION_STORAGE_KEYS.VC_DOCTOR_JOINED] &&
      videoCallThreshold[SESSION_STORAGE_KEYS.VC_PATIENT_JOINED] &&
      videoCallThreshold[SESSION_STORAGE_KEYS.VC_CALL_DURATION_WITH_PATIENT] >
        60
    ) {
      import.meta.env && console.log("Can change appt call status");
      setCanChangeApptCallStatus(true);
    }

    // If doctor and patient joins, start recording timer
    let interval: NodeJS.Timeout | undefined;
    if (
      videoCallThreshold[SESSION_STORAGE_KEYS.VC_DOCTOR_JOINED] &&
      videoCallThreshold[SESSION_STORAGE_KEYS.VC_PATIENT_JOINED]
    ) {
      interval = setInterval(() => {
        setVideoCallThreshold(prev => ({
          ...prev,
          [SESSION_STORAGE_KEYS.VC_CALL_DURATION_WITH_PATIENT]:
            prev[SESSION_STORAGE_KEYS.VC_CALL_DURATION_WITH_PATIENT] + 1,
        }));
      }, 1000);
    }

    // If doctor or patient leaves, stop recording timer
    else if (
      !videoCallThreshold[SESSION_STORAGE_KEYS.VC_DOCTOR_JOINED] ||
      !videoCallThreshold[SESSION_STORAGE_KEYS.VC_PATIENT_JOINED]
    ) {
      clearInterval(interval);
    }

    return () => {
      clearInterval(interval);
    };
  }, [videoCallThreshold]);

  useEffect(() => {
    if (hasCallEnded && canOpenFeedbackModal) {
      // openVideoCallFeedbackModal(); // Hide this for now
      setCanOpenFeedbackModal(false);
    }

    if (hasCallEnded && canChangeApptCallStatus) {
      const jsFormData = new FormData();
      jsFormData.append(
        "details",
        JSON.stringify({ t_call_status: "complete", t_call_time: callDuration })
      );
      updateAppointment({
        appointmentId,
        formData: jsFormData,
      });
      setCanChangeApptCallStatus(false);
    }

    if (hasCallEnded) {
      setVideoCallThreshold({
        [SESSION_STORAGE_KEYS.VC_PATIENT_JOINED]: false,
        [SESSION_STORAGE_KEYS.VC_DOCTOR_JOINED]: false,
        [SESSION_STORAGE_KEYS.VC_CALL_DURATION_WITH_PATIENT]: 0,
      });
      removeThreshold();
    }
  }, [hasCallEnded, canOpenFeedbackModal, canChangeApptCallStatus]);

  return { removeThreshold, setVideoCallThreshold, videoCallThreshold };
};
